import { useLayoutEffect, useRef, useState } from "react";
import { GANTT_ROW_MOTION_MS } from "./ganttConstants";

/** The JS twin of GANTT_ROW_MOTION_EASING. */
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

interface Tween {
  from: number;
  to: number;
  start: number;
}

function valueAt(tween: Tween, now: number): number {
  const t = Math.min(1, (now - tween.start) / GANTT_ROW_MOTION_MS);
  return tween.from + (tween.to - tween.from) * easeOutCubic(t);
}

/** Still moving — with a frame's grace, so the last paint lands exactly on the
 *  target rather than a hair short of it. */
function isMoving(tween: Tween, now: number): boolean {
  return tween.from !== tween.to && now - tween.start < GANTT_ROW_MOTION_MS + 20;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Row tops for the dependency arrows, moving the way the rows do.
 *
 * Rows glide with a CSS transform transition; an arrow is one SVG path over
 * the whole chart and cannot. Drawn from the target tops straight away, every
 * arrow would jump to where its bars are going while the bars were still on
 * their way — so the arrows tween over the same duration and curve instead,
 * retargeting from wherever they are when the order changes mid-glide, as a
 * CSS transition does.
 *
 * @param targets    where each issue's row is going
 * @param immediate  issues whose rows follow the pointer and must not lag it
 * @param resetKey   changes when rows are REMOUNTED rather than moved (a new
 *                   grouping): those rows snap, so the arrows must too
 */
export function useAnimatedRowTops(
  targets: ReadonlyMap<number, number>,
  immediate: ReadonlySet<number>,
  resetKey: string
): ReadonlyMap<number, number> {
  const tweens = useRef(new Map<number, Tween>());
  const lastReset = useRef(resetKey);
  const frame = useRef<number | null>(null);
  const [, repaint] = useState(0);

  useLayoutEffect(() => {
    const now = performance.now();
    const snap = lastReset.current !== resetKey || prefersReducedMotion();
    lastReset.current = resetKey;
    const next = new Map<number, Tween>();
    let snapped = false;
    for (const [id, to] of targets) {
      const existing = tweens.current.get(id);
      if (!existing || snap || immediate.has(id)) {
        next.set(id, { from: to, to, start: now });
        // The render just done drew a row with no tween, or a pointer-driven
        // one, at `to` already; only a snapped tween was drawn somewhere else.
        if (existing && !immediate.has(id) && (existing.to !== to || isMoving(existing, now))) {
          snapped = true;
        }
      } else if (existing.to !== to) {
        next.set(id, { from: valueAt(existing, now), to, start: now });
      } else {
        next.set(id, existing);
      }
    }
    tweens.current = next;

    const animating = () => {
      const at = performance.now();
      return [...tweens.current.values()].some((tween) => isMoving(tween, at));
    };
    if (frame.current === null && animating()) {
      const tick = () => {
        repaint((n) => n + 1);
        frame.current = animating() ? requestAnimationFrame(tick) : null;
      };
      frame.current = requestAnimationFrame(tick);
    }
    // The render that brought these targets read the PREVIOUS tweens, so it
    // drew each snapped arrow where its row used to be. A snap starts no
    // animation, so no frame was coming to repaint it: the stale drawing stayed
    // up until something unrelated re-rendered the chart — arrows joining the
    // wrong rows after a regroup. A state update in a layout effect re-renders
    // before paint, so the stale frame is never seen.
    if (snapped) repaint((n) => n + 1);
  }, [targets, immediate, resetKey]);

  useLayoutEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    []
  );

  const now = performance.now();
  const current = new Map<number, number>();
  for (const [id, to] of targets) {
    const tween = tweens.current.get(id);
    current.set(id, tween && !immediate.has(id) ? valueAt(tween, now) : to);
  }
  return current;
}
